// constants
import { DEFAULT_PLAN_COLUMNS, PLAN_COLUMN_IDS } from 'constants/planColumns'
// types
import { IPlanColumn, IPlanColumnSettings } from 'types/plan/columns'

const toWidth = (width?: string | number): number | undefined => {
  const value = Number(width)

  return Number.isFinite(value) && value > 0 ? value : undefined
}

// `Plan.columnSettings` -> columns the table renders. An absent setting means "never configured"
// and falls back to the defaults, while an explicit empty list means "all columns removed".
export const parseColumnSettings = (columnSettings?: IPlanColumnSettings): IPlanColumn[] => {
  const columns = columnSettings?.columns

  if (!Array.isArray(columns)) {
    return DEFAULT_PLAN_COLUMNS
  }

  return columns.map(({ fieldId, width }) => {
    const parsedWidth = toWidth(width)

    return parsedWidth ? { fieldId, width: parsedWidth } : { fieldId }
  })
}

interface IPlanColumnVariables {
  withCommencementAt: boolean
  withActualCompletionDate: boolean
  withResourceBudget: boolean
  withResourceCost: boolean
}

// `@include(if:)` flags for PLAN_DETAILS_TREE_QUERY — a field is only asked for when the plan
// actually shows its column.
export const getPlanColumnVariables = (columnSettings?: IPlanColumnSettings): IPlanColumnVariables => {
  const columns = parseColumnSettings(columnSettings)
  const hasColumn = (id: string) => columns.some(({ fieldId }) => fieldId === id)

  return {
    withCommencementAt: hasColumn(PLAN_COLUMN_IDS.COMMENCEMENT_AT),
    withActualCompletionDate: hasColumn(PLAN_COLUMN_IDS.ACTUAL_COMPLETION_DATE),
    withResourceBudget: hasColumn(PLAN_COLUMN_IDS.TOTAL_RESOURCE_BUDGET),
    withResourceCost: hasColumn(PLAN_COLUMN_IDS.TOTAL_RESOURCE_COST)
  }
}

// Columns -> the JSON stored on the plan. Order is the order of the array; widths are numbers,
// and an unset width is omitted entirely.
export const toColumnSettings = (columns: IPlanColumn[] = []): IPlanColumnSettings => ({
  columns: columns.map(({ fieldId, width }) => {
    const parsedWidth = toWidth(width)

    return parsedWidth ? { fieldId, width: parsedWidth } : { fieldId }
  })
})
